import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, LogOut, Pencil, X, Check, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';

export default function Profile() {
    const navigate = useNavigate();
    const { user: profile, logout, updateUser } = useAuth();

    // ── Edit modal state ──
    const [isEditing, setIsEditing] = useState(false);
    const [editName, setEditName] = useState('');
    const [editEmail, setEditEmail] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState(false);

    const handleLogout = () => {
        logout();
        navigate('/login', { replace: true });
    };

    const openEditModal = () => {
        setEditName(profile?.name ?? '');
        setEditEmail(profile?.email ?? '');
        setSaveError(null);
        setSaveSuccess(false);
        setIsEditing(true);
    };

    const closeEditModal = () => {
        if (isSaving) return;
        setIsEditing(false);
        setSaveError(null);
    };

    const handleSave = async () => {
        const trimmedName = editName.trim();
        const trimmedEmail = editEmail.trim();

        if (!trimmedName) {
            setSaveError('El nombre no puede estar vacío.');
            return;
        }
        if (trimmedName.length > 100) {
            setSaveError('El nombre no puede superar los 100 caracteres.');
            return;
        }
        if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
            setSaveError('Introduce un correo electrónico válido.');
            return;
        }

        // Skip API call if nothing changed
        if (trimmedName === profile?.name && trimmedEmail === profile?.email) {
            setIsEditing(false);
            return;
        }

        setIsSaving(true);
        setSaveError(null);

        try {
            await authService.updateUserProfile(trimmedName, trimmedEmail);

            if (profile) {
                updateUser({ ...profile, name: trimmedName, email: trimmedEmail });
            }

            setSaveSuccess(true);
            setTimeout(() => {
                setIsEditing(false);
                setSaveSuccess(false);
            }, 1200);
        } catch (err: any) {
            setSaveError(err.message ?? 'Error al guardar. Inténtalo de nuevo.');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <div className="flex flex-col items-center px-5 pt-12">
                {/* User Card */}
                <div className="w-full rounded-2xl border border-gray-100 bg-white p-6 text-center shadow-sm">
                    {/* Avatar */}
                    <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-indigo-100">
                        <User size={36} className="text-indigo-600" />
                    </div>

                    {profile ? (
                        <>
                            <p className="text-lg font-bold text-gray-900">{profile.name}</p>
                            <p className="text-sm text-gray-500">{profile.email}</p>

                            <button
                                id="edit-profile-btn"
                                onClick={openEditModal}
                                className="mt-4 mx-auto flex items-center gap-1.5 text-xs font-medium text-indigo-500 transition-colors hover:text-indigo-700"
                            >
                                <Pencil size={13} />
                                Editar perfil
                            </button>
                        </>
                    ) : (
                        <p className="text-sm text-gray-400">No se pudo cargar el perfil.</p>
                    )}
                </div>

                {/* Logout */}
                <button
                    onClick={handleLogout}
                    className="mt-6 flex w-full cursor-pointer items-center justify-center gap-2 rounded-2xl border border-red-200 bg-red-50 py-3.5 text-sm font-semibold text-red-600 transition-all hover:bg-red-100 active:scale-[0.98]"
                >
                    <LogOut size={18} />
                    Cerrar sesión
                </button>
            </div>

            {/* ── Edit Modal (bottom sheet) ── */}
            {isEditing && (
                <div
                    className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm"
                    onClick={(e) => { if (e.target === e.currentTarget) closeEditModal(); }}
                >
                    <div className="w-full max-w-md rounded-t-3xl bg-white px-6 pb-10 pt-4 shadow-xl">
                        {/* Handle bar */}
                        <div className="mx-auto mb-5 h-1 w-10 rounded-full bg-gray-200" />

                        {/* Header */}
                        <div className="mb-6 flex items-center justify-between">
                            <h2 className="text-base font-semibold text-gray-900">Editar perfil</h2>
                            <button
                                id="close-edit-modal-btn"
                                onClick={closeEditModal}
                                disabled={isSaving}
                                className="text-gray-400 transition-colors hover:text-gray-600 disabled:opacity-40"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Fields */}
                        <div className="flex flex-col gap-4">
                            <div>
                                <label className="mb-1.5 block text-xs font-medium text-gray-500">
                                    Nombre
                                </label>
                                <input
                                    id="edit-name-input"
                                    type="text"
                                    value={editName}
                                    onChange={(e) => { setEditName(e.target.value); setSaveError(null); }}
                                    disabled={isSaving}
                                    placeholder="Tu nombre"
                                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-indigo-400 focus:bg-white disabled:opacity-50"
                                />
                            </div>

                            <div>
                                <label className="mb-1.5 block text-xs font-medium text-gray-500">
                                    Correo electrónico
                                </label>
                                <input
                                    id="edit-email-input"
                                    type="email"
                                    value={editEmail}
                                    onChange={(e) => { setEditEmail(e.target.value); setSaveError(null); }}
                                    disabled={isSaving}
                                    placeholder="tu@correo.com"
                                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-indigo-400 focus:bg-white disabled:opacity-50"
                                />
                            </div>
                        </div>

                        {/* Error */}
                        {saveError && (
                            <p className="mt-3 text-xs font-medium text-red-500">{saveError}</p>
                        )}

                        {/* Save button */}
                        <button
                            id="save-profile-btn"
                            onClick={handleSave}
                            disabled={isSaving || saveSuccess}
                            className="mt-6 flex w-full items-center justify-center gap-2 rounded-2xl bg-indigo-600 py-3.5 text-sm font-semibold text-white transition-all hover:bg-indigo-700 active:scale-[0.98] disabled:opacity-60"
                        >
                            {isSaving ? (
                                <><Loader2 size={16} className="animate-spin" />Guardando...</>
                            ) : saveSuccess ? (
                                <><Check size={16} />¡Guardado!</>
                            ) : (
                                'Guardar cambios'
                            )}
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
